import _ from 'lodash';
import { UncontrolledTooltip } from 'reactstrap';
import {
  IconButton,
  DropdownInput,
  BlackDeleteIcon as blackDeleteIcon,
  BlackExpandHorizontalIcon as blackExpandHorizontalIcon,
  BlackNoiseIcon as blackNoiseIcon,
  BlackUndoIcon as blackUndoIcon,
  ChangeEventIcon as changeEventIcon,
  ReviewedIcon16 as reviewMarkIcon,
  UnreviewedIcon as reviewUnmarkIcon,
} from '@/shared/ui';
import { CANNOT_CHANGE_TYPE_EVENT, TYPE_BEAT_ENUM, TYPE_RHYTHM_EVENT_ENUM } from '@/shared/config';
import { defaultControlModalValue } from '@/entities/report';
import { EventThumbnailConfig } from '@/entities/ecg';
import BulkActionByEvent from '@/features/bulk-event-action';
import FilterEvents from '@/features/filter-events';
import PaginationContainer from './paginationContainer';
import useButtonTypes from './useButtonTypes';

/**
 * Toolbar for the ECG events tab holding the type, artifact, delete, duration, review and reset actions plus filtering and pagination. Reads everything from useButtonTypes, hides the change-type button for event types that cannot be retyped, shows the delete, duration and reset buttons only when their flags allow, disables the row while a strip is loading, and adds a special-event-type dropdown for tachy, brady and pause.
 *
 * @returns The events toolbar with its action buttons, thumbnail configuration, filters and pagination.
 */
const ButtonTypes = () => {
  const {
    keyRecoil,
    isLoadingStrip,
    activeButton,
    isShowDeleteButton,
    isShowChangeEventDurationButton,
    isEventReviewed,
    isShowResetButton,
    isDisableChangingButton,
    isDisableChangeDuration,
    groupTypeChange,
    eventSpecialTypeOptions,
    specialEventTypes,
    onChangeSpecialEventType,
    setControlModal,
    getControlModal,
    handleChangeStripType,
    onClickMarkReview,
    onClickReset,
    setPageIndex,
  } = useButtonTypes();

  return (
    <div id="button-types" className="rhythm-event-content-top-container --min-width-strip-event">
      <div className="toolbar">
        {!CANNOT_CHANGE_TYPE_EVENT.includes(activeButton as any) && (
          <>
            <IconButton
              id="btnChangeEventTypeRhythmEvents"
              isOutline
              iconComponent={<img src={changeEventIcon} alt="Change Event icon" />}
              disabled={isDisableChangingButton}
              onClick={() => {
                setControlModal({
                  ...defaultControlModalValue,
                  isShowChangeTypeBulk: true,
                });
              }}
            />
            <UncontrolledTooltip
              className="custom-tooltip"
              target="btnChangeEventTypeRhythmEvents"
              placement="bottom"
              delay={{ show: 0, hide: 0 }}
              hideArrow
            >
              <div className="beat-tooltip">Change event type</div>
            </UncontrolledTooltip>
          </>
        )}
        <IconButton
          id="btnChangeArtifactRhythmEvents"
          isOutline
          iconComponent={<img src={blackNoiseIcon} alt="Noise icon" />}
          disabled={isDisableChangingButton}
          onClick={() => {
            handleChangeStripType(TYPE_RHYTHM_EVENT_ENUM.ARTIFACT);
          }}
        />
        <UncontrolledTooltip
          className="custom-tooltip"
          target="btnChangeArtifactRhythmEvents"
          placement="bottom"
          delay={{ show: 0, hide: 0 }}
          hideArrow
        >
          <div className="beat-tooltip">
            Mark as artifact
            <span className="shortkey">A</span>
          </div>
        </UncontrolledTooltip>
        {isShowDeleteButton && (
          <>
            <IconButton
              id="btnDeleteRhythmEvents"
              isOutline
              iconComponent={<img src={blackDeleteIcon} alt="Delete icon" />}
              disabled={isDisableChangingButton}
              onClick={() => {
                handleChangeStripType(TYPE_BEAT_ENUM.DELETED);
              }}
            />
            <UncontrolledTooltip
              className="custom-tooltip"
              target="btnDeleteRhythmEvents"
              placement="bottom"
              delay={{ show: 0, hide: 0 }}
              hideArrow
            >
              <div className="beat-tooltip">
                Delete event
                <span className="shortkey">D</span>
              </div>
            </UncontrolledTooltip>
          </>
        )}
        {isShowChangeEventDurationButton && (
          <>
            <IconButton
              id="btnChangeEventDurationRhythmEvents"
              isOutline
              iconComponent={<img src={blackExpandHorizontalIcon} alt="Expand horizontal icon" />}
              disabled={isDisableChangeDuration}
              onClick={() => {
                if (!getControlModal().isShowChangeDurationButtonType) {
                  setControlModal({
                    ...defaultControlModalValue,
                    isShowChangeDurationButtonType: true,
                  });
                }
              }}
            />
            <UncontrolledTooltip
              className="custom-tooltip"
              target="btnChangeEventDurationRhythmEvents"
              placement="bottom"
              delay={{ show: 0, hide: 0 }}
              hideArrow
            >
              <div className="beat-tooltip">
                Change event duration
                <span className="shortkey">C</span>
              </div>
            </UncontrolledTooltip>
          </>
        )}
        <IconButton
          id="btnMarkReviewRhythmEvents"
          isOutline
          iconComponent={
            <img src={isEventReviewed ? reviewMarkIcon : reviewUnmarkIcon} alt="Review mark icon" />
          }
          disabled={isDisableChangingButton}
          onClick={() => {
            onClickMarkReview(!isEventReviewed);
          }}
        />
        <UncontrolledTooltip
          className="custom-tooltip"
          target="btnMarkReviewRhythmEvents"
          placement="bottom"
          delay={{ show: 0, hide: 0 }}
          hideArrow
        >
          <div className="beat-tooltip">
            {isEventReviewed ? 'Unmark as reviewed' : 'Mark as reviewed'}
            <span className="shortkey">R</span>
          </div>
        </UncontrolledTooltip>
        {!!isShowResetButton && (
          <>
            <div className="btn-type-vertical-line" />
            <IconButton
              id="btnResetRhythmEvents"
              isOutline
              iconComponent={<img src={blackUndoIcon} alt="Noise icon" />}
              disabled={isDisableChangingButton}
              onClick={onClickReset}
            />
            <UncontrolledTooltip
              className="custom-tooltip"
              target="btnResetRhythmEvents"
              placement="bottom"
              delay={{ show: 0, hide: 0 }}
              hideArrow
            >
              <div className="beat-tooltip">Reset changes</div>
            </UncontrolledTooltip>
          </>
        )}
      </div>
      <EventThumbnailConfig isDisabled={isLoadingStrip} />
      <div className="toolbar-container">
        <BulkActionByEvent
          isDisabled={isLoadingStrip}
          keyRecoil={keyRecoil}
          activeButton={activeButton}
          hasChanged={groupTypeChange}
          className="mr3"
        />
        {_.includes(
          [
            TYPE_RHYTHM_EVENT_ENUM.TACHY,
            TYPE_RHYTHM_EVENT_ENUM.BRADY,
            TYPE_RHYTHM_EVENT_ENUM.PAUSE,
          ],
          activeButton,
        ) && (
          <DropdownInput
            dropdownToggleId="ecg-events-special-event-type-dropdown-toggle"
            className="mr3"
            name={activeButton as any}
            options={eventSpecialTypeOptions}
            value={(specialEventTypes as any)[activeButton] || ''}
            onChange={onChangeSpecialEventType}
            disabled={isLoadingStrip}
          />
        )}
        <FilterEvents
          activeButton={activeButton}
          keyRecoil={keyRecoil}
          isDisabled={isLoadingStrip}
          setPageIndex={setPageIndex}
        />
        <PaginationContainer
          isDisabled={isLoadingStrip}
          activeButton={activeButton}
          specialEventTypes={specialEventTypes}
        />
      </div>
    </div>
  );
};

export default ButtonTypes;
